import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useStore } from '@tanstack/react-form'
import { createFileRoute } from '@tanstack/react-router'
import clsx from 'clsx'
import { useAppForm, FormField, TextInput, SelectInput } from '@tandem/ui/form'
import $type from '@tandem/ui/type.css'
import $table from '@tandem/ui/table.css'
import Spinner from '@tandem/ui/Spinner'
import { useTRPC } from '@/trpc'
import { useAuth } from '@/auth/AuthContext'
import CounterpartyModal from '../CounterpartyModal'
import { fmt } from '../types'
import $ from './-Counterparties.css'
import $layout from '@tandem/ui/layout.css'
import $anim from '@tandem/ui/anim.css'

export const Route = createFileRoute('/proexpert/')({
  component: CounterpartiesPage,
})

let SHORT: Record<string, string> = { sez: 'СЭЗ', iii: 'ИИИ', licrm: 'РПН', egrul: 'ЕГРЮЛ' }

function CounterpartiesPage() {
  let { company } = useAuth()
  let trpc = useTRPC()
  let enabled = company?.features?.includes('proexpert') ?? false

  let { data: meta = [] } = useQuery({ ...trpc.proexpert.registries.queryOptions(), enabled, staleTime: Infinity })
  let { data: rows = [], isFetching } = useQuery({ ...trpc.proexpert.counterparty.list.queryOptions(), enabled })
  let { data: status } = useQuery({ ...trpc.proexpert.status.queryOptions(), enabled, refetchInterval: 5000 })

  let form = useAppForm({
    defaultValues: {
      search: '',
      registry: '' as string | undefined,
      presence: 'has',
      changed: '' as string | undefined,
      risky: false,
    },
  })
  let search = useStore(form.store, (s) => s.values.search)
  let registry = useStore(form.store, (s) => s.values.registry)
  let presence = useStore(form.store, (s) => s.values.presence)
  let changed = useStore(form.store, (s) => s.values.changed)
  let risky = useStore(form.store, (s) => s.values.risky)

  let [selected, setSelected] = useState<string | null>(null)

  let progress = status?.progress?.state === 'running' ? status.progress : null
  let syncing = progress?.phase === 'registries' ? progress.inn : null

  let filtered = useMemo(() => {
    let q = search.trim().toLowerCase()
    let cutoff = changed ? Date.now() - Number(changed) * 86_400_000 : 0
    return rows
      .filter((r) => !q || r.inn.includes(q) || (r.company_name ?? '').toLowerCase().includes(q))
      .filter((r) => !registry || ((r.registries[registry] ?? 0) > 0) === (presence === 'has'))
      .filter((r) => !cutoff || (r.last_diff_at != null && Date.parse(r.last_diff_at) >= cutoff))
      .filter((r) => !risky || r.risky)
      .sort((a, b) => (a.company_name ?? '').localeCompare(b.company_name ?? '', 'ru'))
  }, [rows, search, registry, presence, changed, risky])

  if (!enabled) return <p class={$type.muted}>Раздел недоступен.</p>

  return (
    <div class={$.page}>

      <div class={clsx($layout.container, $layout.center)}>
        <h3>Контрагенты</h3>

        <div class={$.filters}>
          <form.Field name='search'>
            {(field) => (
              <FormField field={field} label='Поиск по названию или ИНН'>
                <TextInput field={field} />
              </FormField>
            )}
          </form.Field>
          <form.Field name='registry'>
            {(field) => (
              <FormField field={field} label='Реестр'>
                <SelectInput
                  field={field}
                  options={meta.map((m) => ({ value: m.label, label: m.name }))}
                />
              </FormField>
            )}
          </form.Field>
          {registry && (
            <form.Field name='presence'>
              {(field) => (
                <FormField field={field} label='Наличие'>
                  <SelectInput
                    field={field}
                    showEmpty={false}
                    options={[
                      { value: 'has', label: 'есть в реестре' },
                      { value: 'none', label: 'нет в реестре' },
                    ]}
                  />
                </FormField>
              )}
            </form.Field>
          )}
          <form.Field name='changed'>
            {(field) => (
              <FormField field={field} label='Изменения'>
                <SelectInput
                  field={field}
                  options={[
                    { value: '7', label: 'за неделю' },
                    { value: '30', label: 'за месяц' },
                    { value: '90', label: 'за 3 месяца' },
                  ]}
                />
              </FormField>
            )}
          </form.Field>
          <form.Field name='risky'>
            {(field) => (
              <label class={$.riskCheck}>
                <input type='checkbox' checked={field.state.value} onChange={(e) => field.handleChange(e.target.checked)} />
                <span>с рисками</span>
              </label>
            )}
          </form.Field>
        </div>

        <span class={clsx($type.small, $type.muted)}>
          {progress ?
            <>
              <Spinner />{' '}
              {progress.phase === 'freshoffice' ?
                'Синхронизация данных с FreshOffice…'
              : `Синхронизация с реестрами… (${progress.i}/${progress.total})`}
            </>
          : status?.last_sync_at ?
            `Синхронизировано: ${fmt(status.last_sync_at)}`
          : null}
        </span>
      </div>

      {filtered.length > 0 ? (
        <div class={$.tableScroller}>
        <table class={clsx($.table, $table.table, $table.condensed, $type.small)}>
          <thead>
            <tr>
              <th>Контрагент</th>
              <th>ИНН</th>
              {meta.map((m) => (
                <th key={m.label} title={m.name} class='text-center'>{SHORT[m.label] ?? m.label.toUpperCase()}</th>
              ))}
              <th>Синхронизирован</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr
                key={r.inn}
                class={clsx($.row, r.inn === selected && $.selected, r.inn === syncing && $anim.pulse)}
                onClick={() => setSelected(r.inn === selected ? null : r.inn)}
              >
                <td>{r.company_name ?? '—'}</td>
                <td>{r.inn}</td>
                {meta.map((m) => {
                  let found = r.registries[m.label]
                  return (
                    <td key={m.label} class='text-center'>
                      {found == null ? null : found || ''}
                    </td>
                  )
                })}
                <td>{fmt(r.synced_at ?? r.updated_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      ) : (
        <p class={$type.muted} style={{ marginTop: '1em' }}>
          {isFetching ? <Spinner /> : rows.length > 0 ? 'Ничего не найдено' : 'Нет контрагентов'}
        </p>
      )}

      {selected && <CounterpartyModal inn={selected} meta={meta} onClose={() => setSelected(null)} />}
    </div>
  )
}
